import {alias, createModelSchema, deserialize, list, serialize} from "serializr";
import {BaseObject} from "@/common/BaseObject";
import {Schema} from "@/common/Schema";

import {BoatKind} from "./BoatKind";
import {Currency} from "../currency/Currency";
import {RewardModel} from "../reward/RewardModel";
import {GuardConfig} from "../guard/GuardConfig";

import {Range} from "@/modules/redactor/models/utils/Range";
import {ItemKey} from "@/modules/redactor/models/item/core/ItemKey";
import {GuardRule} from "@/modules/redactor/models/guard/GuardRule";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";

export class BoatOrder extends BaseObject {

    constructor() {
        super();

        this._key = null;
        this._name = null;

        this._boat = BoatKind.ANY;
        this._guard = new GuardConfig();
        this._order = 0;

        this._price = [];
        this._priceGuard = false;
        this._reward = new RewardModel();

        this._adPlacement = null;
    }

    get key() {
        return this._key;
    }

    set key(value) {
        this._key = value;
    }

    get name() {
        return this._name;
    }

    set name(value) {
        this._name = value;
    }

    get boat() {
        return this._boat;
    }

    set boat(value) {
        this._boat = value;
    }

    get order() {
        return this._order;
    }

    set order(value) {
        this._order = value;
    }

    get guard() {
        return this._guard;
    }

    set guard(value) {
        this._guard = value;
    }

    get price() {
        return this._price;
    }

    set price(value) {
        this._price = value;
    }

    get priceGuard() {
        return this._priceGuard;
    }

    set priceGuard(value) {
        this._priceGuard = value;
    }

    get reward() {
        return this._reward;
    }

    set reward(value) {
        this._reward = value;
    }

    get adPlacement() {
        return this._adPlacement;
    }

    set adPlacement(value) {
        this._adPlacement = value;
    }

    valid() {

        if (!Number.isInteger(this.order) || this.order < 0) {
            return false;
        }

        if (!BoatKind.is(this.boat)) {
            return false;
        }

        if (this.guard && !this.guard.valid()) {
            return false;
        }

        if (this.price.length === 0 || this.price.some(v => !v || !v.valid())) {
            return false;
        }

        if (this.reward && !this.reward.valid()) {
            return false;
        }

        if (this.adPlacement && !RedactorUtils.getAdPlacement(this.adPlacement)) {
            return false;
        }

        return true;
    }

    copy() {
        let value = super.copy();
        value._enabled = false;
        return value;
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new BoatOrder());
        } else if (target instanceof BoatOrder) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }

    mergeFromJSON(data) {
        if (data.uid && this.uid !== data.uid) {
            throw "does not match uid: " + data.uid;
        }

        for (let key in data) {
            if (key === "key") {
                this.key = data[key];
            } else if (key === "name") {
                this.name = data[key];
            } else if (key === "enabled") {
                this.enabled = data[key];
            } else if (key === "order") {
                this.order = data[key];
            } else if (key === "boat") {
                this.boat = BoatKind.getValue(data[key]);
            } else if (key === "price") {
                this.price = deserialize(Currency, data[key]);
            } else if (key === "price_guard") {
                this.priceGuard = data[key];
            } else if (key === "guard_level") {
                this.guard.level = deserialize(Range, data[key]);
            } else if (key === "guard_allow") {
                this.guard.allow = deserialize(GuardRule, data[key]);
            } else if (key === "guard_block") {
                this.guard.block = deserialize(GuardRule, data[key]);
            } else if (key === "reward_items") {
                this.reward.items = deserialize(ItemKey, data[key]);
            } else if (key === "reward_money") {
                this.reward.money = deserialize(Currency, data[key]);
            } else if (key === "reward_package") {
                this.reward.packages = data[key];
            }
        }
    }

    exportToJSON() {
        return {
            "uid": this.uid,
            "key": this.key,
            "name": this.name,
            "enabled": this.enabled ? 1 : 0,

            "boat": this.boat.id,

            "order": this.order,
            "price": serialize(Currency, this.price),
            "price_guard": this.priceGuard,

            "guard_level": serialize(Range, this.guard.level),
            "guard_allow": serialize(GuardRule, this.guard.allow),
            "guard_block": serialize(GuardRule, this.guard.block),

            "reward_items": serialize(ItemKey, this.reward.items),
            "reward_money": serialize(Currency, this.reward.money),
            "reward_package": this.reward.packages
        };
    }
}

createModelSchema(BoatOrder, {
    _key: alias("key", Schema.primitive()),
    _name: alias("name", Schema.primitive()),

    _boat: alias("boat", BoatKind.schema()),
    _order: alias("order", Schema.primitive()),
    _guard: alias("guard", GuardConfig.schema()),

    _price: alias("price", list(Currency.schema())),
    _priceGuard: alias("price_guard", Schema.primitive()),
    _adPlacement: alias("ad_placement", Schema.primitive()),

    _reward: alias("reward", RewardModel.schema())
});

